import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { BookOpen, Award, Play } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDashboard = () => {
  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("token");
    try {
      const [coursesRes, certsRes] = await Promise.all([
        axios.get(`${API}/user/purchased-courses`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/user/certificates`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setCourses(coursesRes.data);
      setCertificates(certsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async (certId) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${API}/user/certificate/download/${certId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${certId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download certificate");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="w-16 h-16 border-4 border-[#0f3e3e] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="user-dashboard">
      <Navbar />

      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-[#0f3e3e] mb-12" data-testid="dashboard-title">
            My Dashboard
          </h1>

          {/* My Courses */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#0f3e3e] mb-6">My Courses</h2>
            {courses.length === 0 ? (
              <Card className="p-8 text-center">
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">You haven't purchased any courses yet</p>
                <Link to="/courses">
                  <Button className="bg-[#0f3e3e] text-white hover:bg-[#0f3e3e]/90">
                    Browse Courses
                  </Button>
                </Link>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {courses.map((course) => (
                  <Card
                    key={course.id}
                    className="bg-white border border-border rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
                    data-testid={`dashboard-course-${course.id}`}
                  >
                    <CardHeader className="p-0">
                      <img
                        src={course.banner}
                        alt={course.title}
                        className="w-full h-48 object-cover"
                      />
                    </CardHeader>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold text-[#0f3e3e] mb-4">
                        {course.title}
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium text-[#0f3e3e]">
                            {course.progress?.percentage || 0}%
                          </span>
                        </div>
                        <Progress
                          value={course.progress?.percentage || 0}
                          className="h-2"
                        />
                        <p className="text-xs text-muted-foreground">
                          {course.progress?.completed_chapters || 0} of{" "}
                          {course.progress?.total_chapters || 0} chapters completed
                        </p>
                      </div>
                    </CardContent>
                    <CardFooter className="p-6 pt-0">
                      <Link to={`/course-player/${course.id}`} className="w-full">
                        <Button
                          className="w-full bg-[#0f3e3e] text-white hover:bg-[#0f3e3e]/90"
                          data-testid={`continue-course-${course.id}`}
                        >
                          <Play className="mr-2 h-4 w-4" />
                          Continue Learning
                        </Button>
                      </Link>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Certificates */}
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#0f3e3e] mb-6">My Certificates</h2>
            {certificates.length === 0 ? (
              <Card className="p-8 text-center">
                <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Complete courses to earn certificates</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {certificates.map((cert) => (
                  <Card
                    key={cert.id}
                    className="bg-white border border-border p-6"
                    data-testid={`certificate-${cert.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#d4ff32] rounded-full flex items-center justify-center flex-shrink-0">
                        <Award className="w-6 h-6 text-[#0f3e3e]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#0f3e3e] mb-1">
                          {cert.course_title}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {new Date(cert.issued_at).toLocaleDateString()}
                        </p>
                        <Button
                          onClick={() => handleDownloadCertificate(cert.id)}
                          variant="outline"
                          size="sm"
                          data-testid={`download-cert-${cert.id}`}
                        >
                          Download PDF
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default UserDashboard;
